import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  Loader2,
  Plus,
  CreditCard,
  Settings,
  Sparkles,
  ShoppingCart,
  TrendingUp,
  Store,
} from "lucide-react";
import request from "../utils/request";
import {
  loadCatalogAndHost,
  updateService,
  bulkUpdateServices,
  createCustomService,
  selectCatalogList,
  selectServicesMap,
  selectCatalogStatus,
  selectHostStatus,
} from "../store/slices/servicesSlice";
import { getHostProperties } from "../store/thunks/hostActions";
import ServiceCard from "../components/services/ServiceCard";
import ServiceConfigDrawer from "../components/services/ServiceConfigDrawer";
import CreateServiceModal from "../components/services/CreateServiceModal";
import {
  trackConciergeSubscribeClick,
  trackConciergeSubscribeError,
  trackConciergeManageBillingClick,
  trackConciergeCancelConfirmed,
  trackConciergeSubscriptionPageView,
  trackConciergeCheckoutReturn,
} from "../utils/hostAnalytics";

import ConciergeStageShell from "../components/services/concierge/ConciergeStageShell";
import StageHero from "../components/services/concierge/StageHero";
import StageServices from "../components/services/concierge/StageServices";
import StagePricing from "../components/services/concierge/StagePricing";
import StageMarketplace from "../components/services/concierge/StageMarketplace";
import StageDecision from "../components/services/concierge/StageDecision";
import { SERVICE_GRID } from "../components/services/concierge/StageServices";
import { resolveHostAccountId } from "../utils/hostAccount";
const FREE_SERVICES = new Set(["grocery_delivery", "grocery-delivery"]);
const MONETIZABLE_KEYS = new Set(["early_checkin", "late_checkout"]);
const DEFAULT_CONCIERGE_PRICING = {
  propertyCount: 0,
  isQuote: true,
  priceInCents: null,
  monthlyPrice: null,
  displayPrice: "Request a Quote",
  ctaLabel: "Request a Quote",
  helperText: "Pricing is calculated on the server from your property count.",
};

export default function ConciergeSubscriptionScreen() {
  const dispatch = useDispatch();
  const { hostInfo } = useSelector((state) => state.host);
  const hostId = resolveHostAccountId(hostInfo);

  const catalog = useSelector(selectCatalogList);
  const servicesMap = useSelector(selectServicesMap);
  const catalogStatus = useSelector(selectCatalogStatus);
  const hostServicesStatus = useSelector(selectHostStatus);
  const hostProps = useSelector((s) => s.hostProperties?.properties || []);
  const hostDetails = useSelector((s) => s.hostDetails?.Info);
  const hostLogo = hostDetails?.logoUrl || hostDetails?.profilePicture || null;

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState("");
  const [searchParams] = useSearchParams();
  const checkoutStatusParam = searchParams.get("status");
  const pageViewSent = useRef(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerService, setDrawerService] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);

  const [previewToggles, setPreviewToggles] = useState({});
  const [stage, setStage] = useState(1);

  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  useEffect(() => {
    if (!hostId) {
      setStatus({
        conciergeEnabled: false,
        status: "none",
        pricing: DEFAULT_CONCIERGE_PRICING,
      });
      setStatusLoading(false);
      setPageError("Unable to determine your host account. Please sign in again.");
      return;
    }
    setStatusLoading(true);
    setPageError("");
    request
      .get(`/hosts/${hostId}/concierge/status`)
      .then(({ data }) => setStatus(data))
      .catch((err) => {
        setStatus({
          conciergeEnabled: false,
          status: "none",
          pricing: DEFAULT_CONCIERGE_PRICING,
        });
        setPageError(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Unable to load subscription status."
        );
      })
      .finally(() => setStatusLoading(false));
  }, [hostId]);

  useEffect(() => {
    if (hostId) {
      dispatch(loadCatalogAndHost(hostId));
      dispatch(getHostProperties(hostId));
    }
  }, [dispatch, hostId]);

  useEffect(() => {
    if (statusLoading || !hostId || !status || pageViewSent.current) return;
    pageViewSent.current = true;
    trackConciergeSubscriptionPageView({
      hostId,
      subscriptionStatus: status.conciergeEnabled ? "active" : "not_subscribed",
    });
  }, [statusLoading, hostId, status]);

  useEffect(() => {
    if (statusLoading || !hostId || !checkoutStatusParam) return;
    trackConciergeCheckoutReturn({ hostId, statusParam: checkoutStatusParam });
  }, [statusLoading, hostId, checkoutStatusParam]);

  const isSubscribed = !!status?.conciergeEnabled;
  const dataLoading =
    statusLoading ||
    catalogStatus === "loading" ||
    hostServicesStatus === "loading";

  const merged = useMemo(() => {
    const catalogKeys = new Set((catalog || []).map((d) => d.key));
    const fromCatalog = (catalog || []).map((def) => {
      const host = servicesMap[def.key] || {};
      return { ...def, hostService: host };
    });
    const hostOnly = Object.entries(servicesMap || {})
      .filter(([key]) => !catalogKeys.has(key) && key.startsWith("custom_"))
      .map(([key, svc]) => ({
        key,
        name: svc.name || key,
        description: svc.description || "",
        category: svc.category || "Custom",
        isCustom: true,
        hostService: svc,
      }));
    return [...fromCatalog, ...hostOnly];
  }, [catalog, servicesMap]);

  const freeServices = useMemo(
    () => merged.filter((s) => FREE_SERVICES.has(s.key)),
    [merged]
  );
  const paidServices = useMemo(
    () => merged.filter((s) => !FREE_SERVICES.has(s.key) && !s.isCustom),
    [merged]
  );
  const customServices = useMemo(
    () => merged.filter((s) => s.isCustom || s.key?.startsWith("custom_")),
    [merged]
  );
  const signatureServices = useMemo(
    () => paidServices.filter((s) => !MONETIZABLE_KEYS.has(s.key)),
    [paidServices]
  );
  const monetizableServices = useMemo(
    () => paidServices.filter((s) => MONETIZABLE_KEYS.has(s.key)),
    [paidServices]
  );

  const enabledPaidCount = useMemo(() => {
    if (!isSubscribed) {
      return Object.values(previewToggles).filter(Boolean).length;
    }
    return paidServices.filter((s) => s.hostService?.enabled).length;
  }, [isSubscribed, paidServices, previewToggles]);

  const homeCount = hostProps.length;
  const pricing = status?.pricing || DEFAULT_CONCIERGE_PRICING;
  const currentPriceInCents = status?.currentPriceInCents ?? null;
  const planLabel =
    currentPriceInCents != null
      ? `$${(currentPriceInCents / 100).toFixed(0)}`
      : null;
  const selectedServices = useMemo(
    () =>
      SERVICE_GRID.filter((service) => !!previewToggles?.[service.key]).map(
        (service) => service.name
      ),
    [previewToggles]
  );

  const handleSubscribe = async (placement = "unknown") => {
    if (!hostId) {
      alert("Unable to determine your host account. Please sign in again.");
      return;
    }
    const priceShownUsd = pricing.priceInCents
      ? pricing.priceInCents / 100
      : null;
    trackConciergeSubscribeClick({
      hostId,
      source: "service_hub",
      tier: pricing.isQuote ? "quote_request" : "property_count",
      placement,
      priceShownUsd,
    });
    setActionLoading(true);
    try {
      if (pricing.isQuote) {
        const { data } = await request.post(
          `/hosts/${hostId}/concierge/request-quote`,
          {
            placement,
            selectedServices,
          }
        );
        alert(
          data?.message ||
            "Quote request sent. Our onboarding team will reach out by email."
        );
        return;
      }

      const { data } = await request.post(
        `/hosts/${hostId}/concierge/subscribe`,
        {}
      );
      if (!data?.url) {
        throw new Error("Checkout session did not return a redirect URL.");
      }
      window.location.href = data.url;
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to start checkout";
      trackConciergeSubscribeError({ hostId, source: "service_hub", message: msg });
      alert(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleManage = async () => {
    if (!hostId) {
      alert("Unable to determine your host account. Please sign in again.");
      return;
    }
    trackConciergeManageBillingClick({ hostId });
    setActionLoading(true);
    try {
      const { data } = await request.post(`/hosts/${hostId}/concierge/portal`);
      if (!data?.url) {
        throw new Error("Billing portal did not return a redirect URL.");
      }
      window.location.href = data.url;
    } catch (err) {
      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to open billing portal"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (
      !window.confirm(
        "Are you sure? Your subscription stays active until the end of the billing period."
      )
    )
      return;
    trackConciergeCancelConfirmed({ hostId });
    setActionLoading(true);
    try {
      await request.post(`/hosts/${hostId}/concierge/cancel`);
      setStatus((prev) => ({ ...prev, cancelAtPeriodEnd: true }));
    } catch (err) {
      alert(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to cancel"
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggle = async (key, next) => {
    if (!isSubscribed) {
      setPreviewToggles((prev) => ({ ...prev, [key]: next }));
      return;
    }
    try {
      await dispatch(
        updateService({ hostId, key, patch: { enabled: next } })
      ).unwrap?.();
    } catch {
      /* optimistic rollback handled by slice */
    }
  };

  const handleConfigure = (service) => {
    setDrawerService(service);
    setDrawerOpen(true);
  };

  const handlePricingChange = (key, nextPricing) => {
    dispatch(updateService({ hostId, key, patch: { pricing: nextPricing } }));
  };

  const handlePropertyScopeChange = (key, selectedIds, applyAll) => {
    const patch = applyAll
      ? { propertyIds: [] }
      : { propertyIds: selectedIds };
    dispatch(updateService({ hostId, key, patch }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    const updates = merged.map((m) => ({
      key: m.key,
      enabled: !!m.hostService?.enabled,
      visibility: "public",
      pricing: m.hostService?.pricing || {},
      propertyIds: m.hostService?.propertyIds || [],
    }));
    await dispatch(bulkUpdateServices({ hostId, updates }));
    setSaving(false);
  };

  const handleCreateService = async (serviceData) => {
    setCreateSubmitting(true);
    try {
      await dispatch(
        createCustomService({
          hostId,
          serviceData: {
            name: serviceData.name,
            description: serviceData.description,
            category: serviceData.category || "Custom",
            pricing: serviceData.pricing,
            enabled: serviceData.enabled,
            visibility: serviceData.visibility || "public",
          },
        })
      ).unwrap();
      setCreateModalOpen(false);
    } catch (err) {
      alert(
        typeof err === "string"
          ? err
          : "Could not create service. Please try again."
      );
    } finally {
      setCreateSubmitting(false);
    }
  };

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center h-[100dvh] w-full bg-[#FAFAFA]">
        <Loader2 className="w-7 h-7 animate-spin text-[#EF4949]" />
      </div>
    );
  }

  if (pageError && !statusLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#FAFAFA] px-6">
        <div className="w-full max-w-lg rounded-3xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-2xl font-bold text-gray-900">Subscription unavailable</h1>
          <p className="mt-3 text-sm leading-6 text-gray-600">{pageError}</p>
        </div>
      </div>
    );
  }

  const statusParam = searchParams.get("status");

  /* ── PRE-SUBSCRIBED: Zero-scroll narrative flow ────────────────────── */
  if (!isSubscribed) {
    return (
      <>
        {/* Success banner (checkout return) */}
        <AnimatePresence>
          {statusParam === "success" && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#10B981] text-white text-[12px] font-bold shadow-lg"
            >
              <CheckCircle2 size={14} />
              Subscription activated.
            </motion.div>
          )}
        </AnimatePresence>

        <ConciergeStageShell
          stage={stage}
          setStage={setStage}
          canAdvance={true}
          onFinish={() => handleSubscribe("stage_shell_finish")}
        >
          {stage === 1 && (
            <StageHero
              onContinue={() => setStage(2)}
              enabledServices={new Set(["grocery-delivery"])}
              hostLogo={hostLogo}
            />
          )}
          {stage === 2 && (
            <StageServices
              previewToggles={previewToggles}
              onToggle={handleToggle}
              hostLogo={hostLogo}
            />
          )}
          {stage === 3 && (
            <StagePricing
              previewToggles={previewToggles}
              onToggle={handleToggle}
              pricing={pricing}
              onSubscribe={handleSubscribe}
              actionLoading={actionLoading}
              hostLogo={hostLogo}
            />
          )}
          {stage === 4 && (
            <StageMarketplace
              onCreate={() => setCreateModalOpen(true)}
              customServices={customServices}
              hostLogo={hostLogo}
              previewToggles={previewToggles}
            />
          )}
          {stage === 5 && (
            <StageDecision
              previewToggles={previewToggles}
              pricing={pricing}
              onSubscribe={handleSubscribe}
              onBack={() => setStage(3)}
              actionLoading={actionLoading}
              hostLogo={hostLogo}
            />
          )}
        </ConciergeStageShell>

        {/* Create service modal (accessible from Stage 4) */}
        <CreateServiceModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSubmit={handleCreateService}
          submitting={createSubmitting}
        />
      </>
    );
  }

  /* ── SUBSCRIBED: Modernized management cockpit ──────────────────────── */
  return (
    <SubscribedCockpit
      statusParam={statusParam}
      status={status}
      planLabel={planLabel}
      enabledPaidCount={enabledPaidCount}
      totalPaidCount={paidServices.length}
      homeCount={homeCount}
      freeServices={freeServices}
      signatureServices={signatureServices}
      monetizableServices={monetizableServices}
      customServices={customServices}
      isSubscribed={isSubscribed}
      onToggle={handleToggle}
      onConfigure={handleConfigure}
      onManage={handleManage}
      onCancel={handleCancel}
      onCreate={() => setCreateModalOpen(true)}
      actionLoading={actionLoading}
      drawerOpen={drawerOpen}
      drawerService={drawerService}
      servicesMap={servicesMap}
      hostProps={hostProps}
      onCloseDrawer={() => setDrawerOpen(false)}
      onPricingChange={handlePricingChange}
      onPropertyScopeChange={handlePropertyScopeChange}
      onSaveAll={handleSaveAll}
      saving={saving}
      createModalOpen={createModalOpen}
      onCloseCreate={() => setCreateModalOpen(false)}
      onCreateSubmit={handleCreateService}
      createSubmitting={createSubmitting}
    />
  );
}

/* ─────────────────────────────────────────────────────────────
   Subscribed management cockpit (modernized, not cinematic)
   ───────────────────────────────────────────────────────────── */
function SubscribedCockpit({
  statusParam,
  status,
  planLabel,
  enabledPaidCount,
  totalPaidCount,
  homeCount,
  freeServices,
  signatureServices,
  monetizableServices,
  customServices,
  isSubscribed,
  onToggle,
  onConfigure,
  onManage,
  onCancel,
  onCreate,
  actionLoading,
  drawerOpen,
  drawerService,
  servicesMap,
  hostProps,
  onCloseDrawer,
  onPricingChange,
  onPropertyScopeChange,
  onSaveAll,
  saving,
  createModalOpen,
  onCloseCreate,
  onCreateSubmit,
  createSubmitting,
}) {
  return (
    <div
      className="min-h-screen bg-[#FAFAFA]"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {statusParam === "success" && (
        <div className="max-w-[1200px] mx-auto px-6 pt-6">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 px-4 py-3 rounded-[16px] bg-[#ECFDF5] border border-[#D1FAE5]"
          >
            <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0" />
            <p className="text-[13px] font-semibold text-[#047857]">
              Subscription activated. Your guests can now book concierge
              services.
            </p>
          </motion.div>
        </div>
      )}

      <div className="max-w-[1200px] mx-auto px-6 pt-6 pb-16">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="inline-flex items-center gap-2 mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
              <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
                Bellhopt Concierge
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ECFDF5] border border-[#D1FAE5] text-[9px] font-bold uppercase tracking-[0.14em] text-[#047857]">
                <span className="w-1 h-1 rounded-full bg-[#10B981]" />
                {status?.cancelAtPeriodEnd ? "Canceling" : "Active"}
              </span>
            </div>
            <h1
              className="text-[30px] laptop:text-[36px] font-extrabold text-[#111111] leading-tight"
              style={{ letterSpacing: -0.6 }}
            >
              Your guest concierge
            </h1>
            <p className="mt-1 text-[13px] text-[#5F6368]">
              Toggle services, configure pricing, and scope to properties.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onManage}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[12px] bg-white border border-[#EAEAEA] text-[13px] font-bold text-[#111111] hover:bg-[#F7F7F8] transition-colors disabled:opacity-60"
            >
              {actionLoading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <>
                  <CreditCard size={13} />
                  Billing
                </>
              )}
            </button>
            <button
              onClick={onCreate}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[12px] bg-[#111111] text-white text-[13px] font-bold hover:bg-black transition-colors"
            >
              <Plus size={13} strokeWidth={2.5} />
              Add offering
            </button>
          </div>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 laptop:grid-cols-4 gap-2.5 mb-7">
          <Stat
            label="Plan"
            value={planLabel ? `${planLabel}/mo` : "—"}
            icon={<CreditCard size={12} />}
          />
          <Stat
            label="Services on"
            value={`${enabledPaidCount} / ${Math.max(
              totalPaidCount,
              enabledPaidCount
            )}`}
            icon={<Settings size={12} />}
          />
          <Stat
            label="Properties"
            value={String(homeCount)}
            icon={<Store size={12} />}
          />
          <Stat
            label={status?.cancelAtPeriodEnd ? "Active until" : "Next billing"}
            value={
              status?.currentPeriodEnd
                ? new Date(status.currentPeriodEnd).toLocaleDateString(
                    "en-US",
                    { month: "short", day: "numeric" }
                  )
                : "—"
            }
            icon={<CheckCircle2 size={12} />}
          />
        </div>

        {/* Cancellation notice */}
        {status?.cancelAtPeriodEnd && (
          <div className="mb-6 rounded-[14px] bg-[#FFFBEB] border border-[#FEF3C7] p-3.5 flex items-center justify-between gap-3">
            <p className="text-[12px] text-[#92400E]">
              Services remain active until{" "}
              <strong className="font-bold">
                {new Date(status.currentPeriodEnd).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </strong>
              .
            </p>
            <button
              onClick={onManage}
              className="text-[11px] font-bold text-[#92400E] hover:underline"
            >
              Manage billing
            </button>
          </div>
        )}

        {/* Included free */}
        <Section
          eyebrow="Included"
          title="Pre-Arrival Grocery Delivery"
          desc="Active on all your properties — no subscription needed."
          accent={<ShoppingCart size={12} className="text-[#10B981]" />}
        />
        <div className="mb-8">
          {freeServices.length > 0 ? (
            freeServices.map((svc) => (
              <ServiceCard
                key={svc.key}
                service={svc}
                hostService={svc.hostService}
                isSubscribed={isSubscribed}
                isFree={true}
                perServicePrice={0}
                onToggle={onToggle}
                onConfigure={onConfigure}
              />
            ))
          ) : (
            <ServiceCard
              service={{
                key: "grocery_delivery",
                name: "Pre-Arrival Grocery Delivery",
                category: "Provisions",
              }}
              hostService={{ enabled: true }}
              isSubscribed={isSubscribed}
              isFree={true}
              perServicePrice={0}
              onToggle={onToggle}
              onConfigure={onConfigure}
            />
          )}
        </div>

        {/* Signature */}
        <Section
          eyebrow="Signature"
          title="Concierge services"
          desc="Toggle on what guests can book. Tap a card to set pricing and scope."
          accent={<Sparkles size={12} className="text-[#EF4949]" />}
        />
        <div className="grid grid-cols-1 laptop:grid-cols-2 gap-3 mb-8">
          {signatureServices.map((svc) => (
            <ServiceCard
              key={svc.key}
              service={svc}
              hostService={svc.hostService}
              isSubscribed={isSubscribed}
              isFree={false}
              perServicePrice={0}
              onToggle={onToggle}
              onConfigure={onConfigure}
            />
          ))}
        </div>

        {/* Earnings */}
        {monetizableServices.length > 0 && (
          <>
            <Section
              eyebrow="Earnings"
              title="Flexible check-in & check-out"
              desc="Charge for early arrivals and late departures. Guests love it."
              accent={<TrendingUp size={12} className="text-[#10B981]" />}
            />
            <div className="grid grid-cols-1 laptop:grid-cols-2 gap-3 mb-8">
              {monetizableServices.map((svc) => (
                <ServiceCard
                  key={svc.key}
                  service={svc}
                  hostService={svc.hostService}
                  isSubscribed={isSubscribed}
                  isFree={false}
                  perServicePrice={0}
                  onToggle={onToggle}
                  onConfigure={onConfigure}
                />
              ))}
            </div>
          </>
        )}

        {/* Your marketplace */}
        <Section
          eyebrow="Your Marketplace"
          title="Your offerings, your margins"
          desc="Rentals, experiences, add-ons. Anything your guests will love."
          accent={<Store size={12} className="text-[#EF4949]" />}
        />
        <div className="grid grid-cols-2 laptop:grid-cols-4 gap-3">
          {customServices.map((svc) => (
            <motion.button
              key={svc.key}
              whileHover={{ y: -3 }}
              onClick={() => onConfigure(svc)}
              className="relative overflow-hidden rounded-[16px] border border-[#EAEAEA] aspect-square bg-white text-left group"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10" />
              <img
                src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&h=600&fit=crop&q=80"
                alt={svc.name}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-0 left-0 right-0 p-3 z-20">
                <h4 className="text-[13px] font-bold text-white truncate">
                  {svc.name}
                </h4>
                {svc.hostService?.pricing?.flatUSD != null && (
                  <p className="text-[11px] font-semibold text-white/80 mt-0.5">
                    ${svc.hostService.pricing.flatUSD}
                  </p>
                )}
              </div>
            </motion.button>
          ))}

          <motion.button
            whileHover={{ y: -3 }}
            onClick={onCreate}
            className="relative rounded-[16px] border-2 border-dashed border-[#EAEAEA] aspect-square bg-[#F7F7F8] hover:bg-white hover:border-[#FECACA] transition-colors flex items-center justify-center"
          >
            <div className="text-center px-3">
              <div className="w-10 h-10 mx-auto mb-2 rounded-[12px] bg-white border border-[#EAEAEA] flex items-center justify-center">
                <Plus size={16} className="text-[#EF4949]" strokeWidth={2.5} />
              </div>
              <p className="text-[12px] font-bold text-[#111111]">Add an offering</p>
              <p className="text-[10px] text-[#888888] mt-0.5 leading-snug">
                Keep 100% on top of platform fees
              </p>
            </div>
          </motion.button>
        </div>

        {/* Cancel link */}
        {!status?.cancelAtPeriodEnd && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={onCancel}
              disabled={actionLoading}
              className="text-[11px] text-[#888888] hover:text-[#EF4949] transition-colors"
            >
              Cancel subscription
            </button>
          </div>
        )}
      </div>

      <ServiceConfigDrawer
        open={drawerOpen}
        service={drawerService}
        hostService={drawerService ? servicesMap[drawerService.key] : null}
        properties={hostProps}
        onClose={onCloseDrawer}
        onToggle={onToggle}
        onPricingChange={onPricingChange}
        onPropertyScopeChange={onPropertyScopeChange}
        onSave={onSaveAll}
        saving={saving}
      />

      <CreateServiceModal
        open={createModalOpen}
        onClose={onCloseCreate}
        onSubmit={onCreateSubmit}
        submitting={createSubmitting}
      />
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="rounded-[14px] bg-white border border-[#EAEAEA] px-3.5 py-3">
      <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">
        {icon}
        {label}
      </div>
      <div
        className="text-[18px] font-extrabold text-[#111111] mt-0.5 tabular-nums"
        style={{ letterSpacing: -0.3 }}
      >
        {value}
      </div>
    </div>
  );
}

function Section({ eyebrow, title, desc, accent }) {
  return (
    <div className="mb-3">
      <div className="inline-flex items-center gap-1.5 mb-1.5">
        {accent}
        <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
          {eyebrow}
        </span>
      </div>
      <h2
        className="text-[19px] laptop:text-[22px] font-extrabold text-[#111111] leading-tight"
        style={{ letterSpacing: -0.3 }}
      >
        {title}
      </h2>
      {desc && <p className="text-[12px] text-[#5F6368] mt-0.5">{desc}</p>}
    </div>
  );
}
